import { PlayerCourseActivityPage } from '@hendrx/core/courses';
import {
  courseLearningObjectTypeQueryKey,
  getCourseLearningObjectType
} from '@hendrx/core/courses/queries/getCourseLearningObjectType';
import {
  getLatestLearningActivitySnapshotId,
  latestLearningActivitySnapshotIdQueryKey
} from '@hendrx/core/learningActivity/queries/getLatestLearningActivitySnapshotId';
import { getLatestPBLSnapshotId, latestPBLSnapshotIdQueryKey } from '@hendrx/core/pbl/queries/getLatestPBLSnapshotId';
import { getPBLSnapshotById, pblSnapshotByIdQueryKey } from '@hendrx/core/pbl/queries/getPBLSnapshotById';
import {
  getLatestPlenarySnapshotId,
  latestPlenarySnapshotIdQueryKey
} from '@hendrx/core/plenary/queries/getLatestPlenarySnapshotId';
import { LearningObjectTypes } from '@hendrx/lms/constants';
import { QueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { FrontendSharedEvents } from 'shared/client/consts/frontendSharedEvents';
import { serializeError } from 'shared/client/utils/serializeError';
import { logFrontendEventApi } from 'shared/queries/logFrontendEventApi';

import { createRouteGuard } from '@/app/_lib/guards/createRouteGuard';
import { anyOf, requireAuthenticated, requirePlenaryCookie } from '@/app/_lib/guards/primitives';
import { getQueryClient } from '@/app/getQueryClient';
import i18n from '@/i18n';

export const Route = createFileRoute('/player/courses/$courseDraftIdentifier/$categoryId/$activityId')({
  beforeLoad: createRouteGuard(anyOf(requireAuthenticated(), requirePlenaryCookie())),
  loader: async ({ params }) => {
    const queryClient = getQueryClient();

    try {
      const typeData = await queryClient.ensureQueryData({
        queryKey: courseLearningObjectTypeQueryKey(params.activityId),
        queryFn: () => getCourseLearningObjectType(params.activityId)
      });

      const type = typeData?.learningObjectType;

      if (type === LearningObjectTypes.LearningActivity) {
        return { snapshot: await loadLearningActivitySnapshot(queryClient, params.activityId) };
      }
      if (type === LearningObjectTypes.PlenaryLesson) {
        return { snapshot: await loadPlenarySnapshot(queryClient, params.activityId) };
      }
      if (type === LearningObjectTypes.PBL) {
        return { snapshot: await loadPBLSnapshot(queryClient, params.activityId) };
      }
    } catch (error) {
      void logFrontendEventApi({
        eventType: FrontendSharedEvents.player.activityLoadError,
        url: window.location.href,
        ...(error instanceof Error ? { serializedError: serializeError(error) } : {})
      });
    }

    return { snapshot: null };
  },
  head: ({ loaderData }) => {
    const snapshot = loaderData?.snapshot;
    const title = snapshot?.title ?? i18n.t('pages.common:headTitle', { defaultValue: 'Hendrx' });

    return {
      meta: [
        {
          title
        },
        {
          property: 'og:title',
          content: title
        }
      ]
    };
  },
  component: PlayerCourseActivityPageComponent
});

/**
 * Warms the latest-snapshot-id cache for `LearningActivityPlayerSetup` and yields no title. The loader
 * runs pre-mount, where only the course *alias* is known — not the course draft id the snapshot guard
 * needs in order to ask the course entitlement question — so fetching the snapshot here answers 403 for
 * any caller entitled through the course subscription rather than per item. `useLearningObjectSEO` sets
 * the title on mount from the `/content` response instead.
 *
 * TODO(535725): return `title` from the LA `latest-id` endpoint, as the plenary one already does, and
 * restore the pre-mount title without fetching content.
 */
async function loadLearningActivitySnapshot(queryClient: QueryClient, activityId: string) {
  // Only prefetch the latest snapshot id, for the same reason as the plenary sibling below: the
  // snapshot itself goes through the BE access guard, which needs course context that does not exist
  // until the LMS toolbar resolves post-mount. Prefetching the content here delivered it to the
  // browser before anything could deny it, so a student holding a link to an activity inside a
  // teachers-only category received it whatever the course said (#529689). The content is fetched
  // post-mount instead, where the course context is known and the guard can act on it.
  //
  // Unlike plenary there is no title to surface — `/latest-id` returns only `_id` — so the page title
  // is left to the player, which sets it once the content arrives.
  await queryClient.ensureQueryData({
    queryKey: latestLearningActivitySnapshotIdQueryKey({ learningActivityDraftId: activityId, publishedOnly: true }),
    queryFn: () => getLatestLearningActivitySnapshotId({ learningActivityDraftId: activityId, publishedOnly: true })
  });

  return null;
}

async function loadPlenarySnapshot(queryClient: QueryClient, activityId: string) {
  // Only prefetch the latest snapshot id + title — the snapshot itself goes through
  // the BE access guard (PBI 527401), which needs `courseEnvironmentId`, not available
  // until the LMS toolbar resolves post-mount. PlenaryPlayerSetup fetches content then.
  // Title is surfaced so route-level head() can render the page title pre-mount.
  const snapshotRef = await queryClient.ensureQueryData({
    queryKey: latestPlenarySnapshotIdQueryKey({ plenaryDraftId: activityId, publishedOnly: true }),
    queryFn: () => getLatestPlenarySnapshotId({ plenaryDraftId: activityId, publishedOnly: true })
  });
  return snapshotRef?.title ? { title: snapshotRef.title } : null;
}

async function loadPBLSnapshot(queryClient: QueryClient, activityId: string) {
  const snapshotRef = await queryClient.ensureQueryData({
    queryKey: latestPBLSnapshotIdQueryKey({ pblDraftId: activityId, publishedOnly: true }),
    queryFn: () => getLatestPBLSnapshotId({ pblDraftId: activityId, publishedOnly: true })
  });
  if (!snapshotRef?._id) return null;
  return queryClient.ensureQueryData({
    queryKey: pblSnapshotByIdQueryKey(snapshotRef._id),
    queryFn: () => getPBLSnapshotById({ pblSnapshotId: snapshotRef._id })
  });
}

function PlayerCourseActivityPageComponent() {
  const { activityId } = Route.useParams();

  return <PlayerCourseActivityPage activityId={activityId} />;
}
